import { NzMessageService } from 'ng-zorro-antd/message';
import sortBy from 'lodash/sortBy';
import { finalize } from 'rxjs/internal/operators/finalize';

import {
  Component, OnInit, OnDestroy, ChangeDetectorRef,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { CompanySwitcherService, ICompany } from './company-switcher.service';
import { CompanySwitcherSessionService } from './company-switcher-session.service';

import { reloadApp } from '@lib/utils';

@Component({
  selector: 'company-switcher',
  templateUrl: './company-switcher.component.html',
  styleUrls: ['./company-switcher.component.scss'],
})

export class CompanySwitcherComponent implements OnInit, OnDestroy {
  public isSwitchingCompany = false;
  public list: ICompany[] = [];
  public showMore = false;
  private readonly defaultVisibleLimit: number = 3;
  private readonly eventListenerKey: string = 'storage';
  private readonly companySwitchedFlag: string = 'company_switched';

  constructor(
    private companySwitcherService: CompanySwitcherService,
    private companySwitcherSessionService: CompanySwitcherSessionService,
    private message: NzMessageService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    window.addEventListener(this.eventListenerKey, this.companySwitched.bind(this));
    this.initCompaniesList();
  }

  public ngOnDestroy(): void {
    window.removeEventListener(this.eventListenerKey, this.companySwitched.bind(this));
    localStorage.removeItem(this.companySwitchedFlag);
  }

  public get canSwitchCompany(): boolean {
    return this.companySwitcherService.canSwitchCompany;
  }

  public get showMoreActive(): boolean {
    return this.list.length > this.defaultVisibleLimit;
  }

  public get companiesList(): ICompany[] {
    return this.showMore ? this.list : this.list.slice(0, this.defaultVisibleLimit);
  }

  public get listItemsLeft(): number {
    return this.list.length - this.defaultVisibleLimit;
  }

  public toggleShowMore(): void {
    this.showMore = !this.showMore;
  }

  public switchCompany(companyId: number): void {
    this.isSwitchingCompany = true;
    this.companySwitcherService
      .switchCompany(companyId)
      .pipe(finalize(() => this.isSwitchingCompany = false))
      .subscribe((token: string) => {
        if (token) {
          this.companySwitcherSessionService.clearCookies().then(() => {
            this.companySwitcherSessionService.setAuthToken(token);
            localStorage.setItem(this.companySwitchedFlag, companyId.toString());
            reloadApp();
          });
        }
      },
      (err: HttpErrorResponse) => {
        if (err.status === 401) {
          this.message.error($localize`Unable to switch organization, please try again.`);
          this.initCompaniesList();
        }
      });
  }

  private initCompaniesList(): void {
    this.companySwitcherService
      .getCompanies()
      .subscribe({
        next: (list: ICompany[]) => {
          this.list = sortBy(list, ({ selected }) => selected ? 0 : 1);
          this.cdr.markForCheck();
        },
        error: () => this.message.error($localize`Failed to fetch organizations, please try reloading the page.`),
      });
  }

  private companySwitched(event: any): void {
    if (event.key == this.companySwitchedFlag && !!event.newValue) {
      reloadApp();
    }
  }
}
